import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api, useTheme } from '../App';
import { GlassCalendar } from '../components/GlassCalendar';
import { AreaChart, Area, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, BarChart, Bar, Cell } from 'recharts';

const DEMO_DATA = [
  { day: 'Mo', wert: 4200 }, { day: 'Di', wert: 3100 }, { day: 'Mi', wert: 5800 },
  { day: 'Do', wert: 4400 }, { day: 'Fr', wert: 6200 }, { day: 'Sa', wert: 3800 }, { day: 'So', wert: 5100 },
];

const PLATFORM_LABELS = { instagram: 'Instagram', linkedin: 'LinkedIn', facebook: 'Facebook', x: 'X', threads: 'Threads', pinterest: 'Pinterest', reddit: 'Reddit', bluesky: 'Bluesky', google_business: 'Google' };
const PLATFORM_COLORS = { instagram: '#e1306c', linkedin: '#0077b5', facebook: '#1877f2', x: '#555', threads: '#888', pinterest: '#e60023', reddit: '#ff4500', bluesky: '#0085ff', google_business: '#ea4335' };

function extractTimeseries(platformData) {
  if (!platformData) return null;
  const ts = platformData.reach_timeseries || platformData.views_timeseries || platformData.impressions_timeseries;
  if (ts?.length >= 2) {
    return ts.slice(-7).map((entry, i) => ({
      day: entry.date ? entry.date.slice(5) : ['Mo','Di','Mi','Do','Fr','Sa','So'][i % 7],
      wert: entry.value || 0,
    }));
  }
  return null;
}

function extractMetricBars(platformData) {
  if (!platformData) return null;
  const bars = [];
  const map = [
    ['Follower',      platformData.followers],
    ['Neue Follower', platformData.new_followers ?? platformData.followers_gained],
    ['Reichweite',    platformData.reach ?? platformData.views],
    ['Impressionen',  platformData.impressions],
    ['Likes',         platformData.likes],
    ['Kommentare',    platformData.comments],
    ['Shares',        platformData.shares ?? platformData.reposts],
    ['Saves',         platformData.saves],
    ['Klicks',        platformData.clicks],
    ['Website-Klicks',platformData.website_clicks],
    ['Profilaufrufe', platformData.profile_visits ?? platformData.profile_views],
    ['Story-Aufrufe', platformData.story_views ?? platformData.story_impressions],
    ['Video-Aufrufe', platformData.video_views ?? platformData.video_plays],
  ];
  for (const [label, val] of map) {
    if (val != null && val > 0) bars.push({ label, wert: val });
  }
  return bars.length > 0 ? bars : null;
}

function extractEngagementRate(platformData) {
  if (!platformData?.engagement_rate) return null;
  const r = platformData.engagement_rate;
  return r > 1 ? r.toFixed(1) : (r * 100).toFixed(1);
}

// ── Onboarding-Checkliste ─────────────────────────────────────────────────────
function OnboardingChecklist({ hasVoiceProfile, hasCompanyProfile, hasChannels, hasFirstPost }) {
  const steps = [
    { id: 'company',  done: hasCompanyProfile, label: 'Firmenprofil ausfüllen',        desc: 'Name, Branche, Zielgruppe — Basis für alle KI-Generierungen.',                link: '/settings?tab=company',  cta: 'Jetzt ausfüllen'    },
    { id: 'voice',    done: hasVoiceProfile,   label: 'Voice Profile anlegen',          desc: '5–10 alte Posts einfügen. KI schreibt danach in deinem Stil.',               link: '/settings?tab=voice',    cta: 'Zum Voice Profile'  },
    { id: 'post',     done: hasFirstPost,      label: 'Ersten Post generieren',         desc: 'Thema eingeben, Plattformen wählen, KI schreibt — fertig.',                   link: '/generate',              cta: 'Post erstellen'     },
    { id: 'channels', done: hasChannels,       label: 'Social-Media-Kanal verbinden',   desc: 'Direkt aus dem Tool posten und planen ohne Copy-Paste.',                      link: '/settings?tab=channels', cta: 'Kanal verbinden'    },
  ];

  const completedCount = steps.filter(s => s.done).length;
  if (completedCount === steps.length) return null;
  const pct = Math.round((completedCount / steps.length) * 100);

  return (
    <div className="fade-up glass" style={{ marginBottom: 22, overflow: 'hidden', animationDelay: '0.04s' }}>
      <div style={{ height: 2, background: 'linear-gradient(90deg, var(--cyan), var(--blue))' }} />
      <div style={{ padding: '18px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
              <span className="dot-live" />
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--cyan)', letterSpacing: '0.1em' }}>EINRICHTUNG</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text2)' }}>{completedCount} von {steps.length} Schritten abgeschlossen</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 120, height: 5, background: 'var(--glass-border)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, var(--cyan), var(--blue))', borderRadius: 3, transition: 'width 0.4s' }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cyan)' }}>{pct}%</span>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {steps.map((step, i) => (
            <div key={step.id} style={{ padding: '12px 14px', borderRadius: 10, background: step.done ? 'rgba(0,255,136,0.05)' : 'var(--glass2)', border: `1px solid ${step.done ? 'rgba(0,255,136,0.2)' : 'var(--glass-border)'}`, opacity: step.done ? 0.7 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: step.done ? 'rgba(0,255,136,0.15)' : 'var(--glass-border)', color: step.done ? 'var(--green)' : 'var(--muted)', border: `1px solid ${step.done ? 'rgba(0,255,136,0.3)' : 'transparent'}` }}>
                  {step.done ? '✓' : i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: step.done ? 'var(--green)' : 'var(--text)', marginBottom: 3 }}>{step.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5, marginBottom: step.done ? 0 : 8 }}>{step.desc}</div>
                  {!step.done && <Link to={step.link}><button className="btn-ghost" style={{ fontSize: 10, padding: '3px 10px' }}>{step.cta} →</button></Link>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { token, user } = useAuth();
  const { theme }    = useTheme();
  const navigate     = useNavigate();
  const [posts,          setPosts]          = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [ideas,          setIdeas]          = useState(null);
  const [ideasSrc,       setIdeasSrc]       = useState('');
  const [genLoading,     setGenLoading]     = useState(false);
  const [genError,       setGenError]       = useState('');
  const [needsProfile,   setNeedsProfile]   = useState(false);
  const [analytics,      setAnalytics]      = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [publishedCount,   setPublishedCount]   = useState(null);
  const [ga4Data,    setGa4Data]    = useState(undefined);
  const [ga4Loading, setGa4Loading] = useState(true);
  const [ga4Days,    setGa4Days]    = useState(7);

  // Onboarding-Status
  const [hasVoiceProfile,  setHasVoiceProfile]  = useState(false);
  const [hasCompanyProfile, setHasCompanyProfile] = useState(false);
  const [hasChannels,      setHasChannels]      = useState(false);
  const [hasFirstPost,     setHasFirstPost]     = useState(false);

  const isDark = theme === 'dark';

  useEffect(() => {
    api('/posts', {}, token).then(p => { setPosts(p); setHasFirstPost(p.length > 0); }).catch(console.error).finally(() => setLoading(false));
    api('/uploadpost/scheduled', {}, token).then(setScheduledPosts).catch(() => {});
    api('/uploadpost/history', {}, token).then(d => setPublishedCount((d.posts || []).filter(p => p.success !== false).length)).catch(() => setPublishedCount(null));
    api('/uploadpost/analytics', {}, token).then(d => { if (d.analytics) { setAnalytics(d); const first = d.platforms?.[0] || Object.keys(d.analytics)[0]; if (first) setSelectedPlatform(first); } }).catch(() => {}).finally(() => setAnalyticsLoading(false));

    // Onboarding-Checks — connected: true reicht, platforms-Array ist optional
    api('/profile', {}, token).then(d => setHasVoiceProfile(!!d?.profile_text)).catch(() => {});
    api('/company', {}, token).then(d => setHasCompanyProfile(!!d?.company_name)).catch(() => {});
    api('/uploadpost/status', {}, token).then(d => setHasChannels(d?.connected === true)).catch(() => {});
  }, [token]);

  useEffect(() => {
    setGa4Loading(true);
    api(`/ga4/data?days=${ga4Days}`, {}, token)
      .then(d => setGa4Data(d))
      .catch(() => setGa4Data(null))
      .finally(() => setGa4Loading(false));
  }, [token, ga4Days]);

  const loadIdeas = useCallback(async () => {
    try {
      const data = await api('/suggestions', {}, token);
      if (data.suggestions?.length > 0) { setIdeas(data.suggestions.slice(0, 5)); setIdeasSrc('weekly'); return; }
    } catch {}
    setIdeas([]); setIdeasSrc('none');
  }, [token]);
  useEffect(() => { loadIdeas(); }, []);

  async function handleGenerate(force = false) {
    setGenLoading(true); setGenError(''); setNeedsProfile(false);
    try {
      const data = await api('/suggestions/generate', { method: 'POST', body: JSON.stringify({ force }) }, token);
      if (data.suggestions?.length > 0) { setIdeas(data.suggestions.slice(0, 5)); setIdeasSrc('weekly'); }
    } catch (err) {
      if (err.message?.includes('Firmenprofil')) setNeedsProfile(true);
      else setGenError(err.message);
    } finally { setGenLoading(false); }
  }

  const total    = posts.length;
  const approved = posts.filter(p => p.variants?.some(v => v.approved)).length;
  const drafts   = total - approved;
  const hour     = new Date().getHours();
  const greeting = hour < 12 ? 'Guten Morgen' : hour < 18 ? 'Guten Tag' : 'Guten Abend';

  const statsData = [
    { title: 'Posts gesamt',    value: String(total),          change: total > 0 ? `${total} erstellt` : '0',                                        positive: true,  icon: '✦', color: 'var(--cyan)'  },
    { title: 'Veroeffentlicht', value: publishedCount !== null ? String(publishedCount) : '—', change: publishedCount > 0 ? `+${publishedCount}` : '—', positive: true,  icon: '⊹', color: 'var(--green)' },
    { title: 'Entwerfe',        value: String(drafts),         change: drafts > 0 ? `${drafts} offen` : '—',                                         positive: false, icon: '◎', color: 'var(--gold)'  },
    { title: 'Geplant',         value: String(scheduledPosts.length), change: scheduledPosts.length > 0 ? `${scheduledPosts.length} ausstehend` : '—',  positive: true,  icon: '📅', color: 'var(--blue2)' },
  ];

  const analyticsPlatforms = analytics?.platforms || [];
  const platformData       = selectedPlatform ? analytics?.analytics?.[selectedPlatform] : null;
  const timeseriesData     = platformData ? extractTimeseries(platformData) : null;
  const metricBarsData     = platformData ? extractMetricBars(platformData) : null;
  const engagementRate     = platformData ? extractEngagementRate(platformData) : null;
  const hasTimeseries      = !!timeseriesData;
  const hasMetricBars      = !!metricBarsData;
  const hasRealData        = !!(platformData && (hasTimeseries || hasMetricBars));
  const showDemoChart      = !hasRealData;
  const chartColor         = PLATFORM_COLORS[selectedPlatform] || '#00ffff';

  // Platform-Verteilung aus eigenen Posts (kein Upload-Post API nötig)
  const pubByPlatform = {};
  posts.forEach(post => {
    (post.variants || []).filter(v => v.published).forEach(v => {
      pubByPlatform[v.platform] = (pubByPlatform[v.platform] || 0) + 1;
    });
  });
  const ownPlatformData = Object.entries(pubByPlatform).sort((a, b) => b[1] - a[1])
    .map(([pl, cnt]) => ({ label: PLATFORM_LABELS[pl] || pl, wert: cnt, color: PLATFORM_COLORS[pl] || '#00ffff' }));

  const fmtDate     = d => d?.length === 8 ? `${d.slice(6)}.${d.slice(4,6)}.` : (d || '');
  const fmtDuration = s => s < 60 ? `${Math.round(s)}s` : `${Math.floor(s/60)}:${String(Math.round(s%60)).padStart(2,'0')}min`;
  const fmtBounce   = r => r > 1 ? `${r.toFixed(0)}%` : `${(r*100).toFixed(0)}%`;
  const ga4Summary  = ga4Data?.summary;
  const ga4Channels = ga4Data?.channels || [];
  const ga4Pages    = ga4Data?.topPages  || [];
  const ga4Series   = (ga4Data?.timeseries || []).map(row => ({ day: fmtDate(row.date), sessions: row.sessions, users: row.users }));

  return (
    <div style={{ padding: '28px 32px', minHeight: '100dvh', maxWidth: 1600 }}>
      <div className="fade-up" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span className="dot-live" />
              <span style={{ fontSize: 12, color: 'var(--cyan)', fontFamily: 'Outfit', letterSpacing: '0.08em', opacity: 0.8 }}>{new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }).toUpperCase()}</span>
            </div>
            <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 6 }}>{greeting}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}. <span className="gradient-text">Bereit?</span></h1>
            <p style={{ color: 'var(--text2)', fontSize: 15 }}>KI schreibt in deiner Stimme. Du behältst die Kontrolle.</p>
          </div>
          <Link to="/generate"><button className="btn-primary" style={{ fontSize: 15, padding: '13px 28px' }}><span>✦</span> Post generieren</button></Link>
        </div>
      </div>

      <OnboardingChecklist hasVoiceProfile={hasVoiceProfile} hasCompanyProfile={hasCompanyProfile} hasChannels={hasChannels} hasFirstPost={hasFirstPost} />

      <div className="fade-up dash-stats" style={{ display: 'grid', gap: 14, marginBottom: 22, animationDelay: '0.05s' }}>
        {statsData.map((s, i) => <StatCard key={i} {...s} delay={i * 0.06} />)}
      </div>

      <div className="fade-up dash-top" style={{ display: 'grid', gap: 16, marginBottom: 16, animationDelay: '0.1s' }}>
        <div className="glass" style={{ padding: '22px 26px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 3 }}>Performance Analytics</h2>
              <p style={{ fontSize: 13, color: 'var(--text2)' }}>
                {analyticsLoading ? 'Laedt...' : hasRealData ? `Daten von ${PLATFORM_LABELS[selectedPlatform] || selectedPlatform}` : analyticsPlatforms.length > 0 ? 'Keine Daten fuer diesen Zeitraum' : 'Beispielansicht — Kanaele verbinden fuer echte Zahlen'}
              </p>
            </div>
            {analyticsPlatforms.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {analyticsPlatforms.map(pl => (
                  <button key={pl} onClick={() => setSelectedPlatform(pl)} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer', border: `1px solid ${selectedPlatform === pl ? (PLATFORM_COLORS[pl] || 'var(--cyan)') + '60' : 'var(--glass-border)'}`, background: selectedPlatform === pl ? `${PLATFORM_COLORS[pl] || 'var(--cyan)'}15` : 'transparent', color: selectedPlatform === pl ? (PLATFORM_COLORS[pl] || 'var(--cyan)') : 'var(--muted)', fontWeight: selectedPlatform === pl ? 600 : 400 }}>
                    {PLATFORM_LABELS[pl] || pl}
                  </button>
                ))}
              </div>
            )}
          </div>
          {hasRealData && platformData && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {[
                ['Follower',       platformData.followers],
                ['Neue Follower',  platformData.new_followers ?? platformData.followers_gained],
                ['Reichweite',     platformData.reach ?? platformData.views],
                ['Impressionen',   platformData.impressions],
                ['Likes',          platformData.likes],
                ['Kommentare',     platformData.comments],
                ['Shares',         platformData.shares ?? platformData.reposts],
                ['Saves',          platformData.saves],
                ['Klicks',         platformData.clicks],
                ['Website',        platformData.website_clicks],
                ['Profilaufrufe',  platformData.profile_visits ?? platformData.profile_views],
                ['Story-Aufrufe',  platformData.story_views ?? platformData.story_impressions],
                ['Video-Aufrufe',  platformData.video_views ?? platformData.video_plays],
              ].filter(([, v]) => v != null && v > 0).map(([l, v]) => (
                <div key={l} style={{ padding: '8px 14px', background: 'var(--glass2)', borderRadius: 9, border: `1px solid ${chartColor}25` }}>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 2 }}>{l}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: chartColor, fontFamily: 'Orbitron' }}>{Number(v).toLocaleString('de-DE')}</div>
                </div>
              ))}
              {engagementRate && (
                <div style={{ padding: '8px 14px', background: 'rgba(255,170,0,0.06)', borderRadius: 9, border: '1px solid rgba(255,170,0,0.2)' }}>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 2 }}>Engagement Rate</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--gold)', fontFamily: 'Orbitron' }}>{engagementRate}%</div>
                </div>
              )}
            </div>
          )}
          <div style={{ flex: 1, minHeight: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              {hasTimeseries ? (
                <AreaChart data={timeseriesData}>
                  <defs><linearGradient id="gReal" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={chartColor} stopOpacity={0.3}/><stop offset="95%" stopColor={chartColor} stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--glass-border)" />
                  <XAxis dataKey="day" stroke="var(--muted)" tick={{ fill: 'var(--text2)', fontSize: 11 }} />
                  <YAxis stroke="var(--muted)" tick={{ fill: 'var(--text2)', fontSize: 11 }} domain={[0, 'auto']} />
                  <Tooltip contentStyle={{ background: isDark ? 'rgba(10,10,20,0.95)' : 'rgba(240,248,255,0.97)', border: `1px solid ${chartColor}40`, borderRadius: 10, fontSize: 13, color: 'var(--text)' }} />
                  <Area type="monotone" dataKey="wert" stroke={chartColor} strokeWidth={2.5} fill="url(#gReal)" name="Reichweite" />
                </AreaChart>
              ) : hasMetricBars ? (
                <BarChart data={metricBarsData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--glass-border)" vertical={false} />
                  <XAxis dataKey="label" stroke="var(--muted)" tick={{ fill: 'var(--text2)', fontSize: 11 }} />
                  <YAxis stroke="var(--muted)" tick={{ fill: 'var(--text2)', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: isDark ? 'rgba(10,10,20,0.95)' : 'rgba(240,248,255,0.97)', border: `1px solid ${chartColor}40`, borderRadius: 10, fontSize: 13, color: 'var(--text)' }} />
                  <Bar dataKey="wert" name="Wert" radius={[6,6,0,0]}>{metricBarsData.map((_, i) => <Cell key={i} fill={chartColor} opacity={0.7 + i * 0.05} />)}</Bar>
                </BarChart>
              ) : (
                <AreaChart data={DEMO_DATA}>
                  <defs><linearGradient id="gDemo" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00ffff" stopOpacity={0.15}/><stop offset="95%" stopColor="#00ffff" stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--glass-border)" />
                  <XAxis dataKey="day" stroke="var(--muted)" tick={{ fill: 'var(--text2)', fontSize: 11 }} />
                  <YAxis stroke="var(--muted)" tick={{ fill: 'var(--text2)', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: isDark ? 'rgba(10,10,20,0.95)' : 'rgba(240,248,255,0.97)', border: '1px solid rgba(0,255,255,0.25)', borderRadius: 10, fontSize: 13 }} />
                  <Area type="monotone" dataKey="wert" stroke="#00ffff" strokeWidth={2} strokeDasharray="5 5" fill="url(#gDemo)" name="Beispieldaten" />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
          {showDemoChart && !analyticsLoading && (
            <p style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 8 }}>Beispielansicht · <Link to="/settings?tab=channels" style={{ color: 'var(--cyan)' }}>Kanaele verbinden</Link> fuer echte Daten</p>
          )}
          {ownPlatformData.length > 0 && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--glass-border)' }}>
              <p style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, letterSpacing: '0.08em', marginBottom: 10 }}>EIGENE VEROEFFENTLICHUNGEN</p>
              {ownPlatformData.map(({ label, wert, color }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                  <div style={{ width: 90, fontSize: 11, color: 'var(--text2)', flexShrink: 0 }}>{label}</div>
                  <div style={{ flex: 1, height: 5, background: 'var(--glass-border)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.round((wert / ownPlatformData[0].wert) * 100)}%`, background: color, borderRadius: 3, opacity: 0.7 }} />
                  </div>
                  <div style={{ width: 20, fontSize: 12, fontWeight: 700, color, textAlign: 'right', flexShrink: 0 }}>{wert}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass" style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 3 }}>KI Content-Ideen</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {ideasSrc === 'weekly' ? <><span className="dot-live" style={{ width: 5, height: 5 }} /><span style={{ fontSize: 11, color: 'var(--green)' }}>KI-generiert · diese Woche</span></> : <span style={{ fontSize: 11, color: 'var(--muted)' }}>Basierend auf deinem Firmenprofil</span>}
              </div>
            </div>
            {ideasSrc === 'weekly' && <button onClick={() => handleGenerate(true)} disabled={genLoading} className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px', flexShrink: 0 }}>{genLoading ? <><Spinner /> Neu...</> : '↻ Neue Ideen'}</button>}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {ideas === null && <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <div key={i} className="shimmer" style={{ height: 58, borderRadius: 10 }} />)}</div>}
            {ideas !== null && needsProfile && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '16px 8px' }}>
                <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.4 }}>🏢</div>
                <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>Firmenprofil fehlt</p>
                <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 14 }}>Fuell dein Firmenprofil aus um passende Ideen zu erhalten.</p>
                <Link to="/settings?tab=company"><button className="btn-primary" style={{ fontSize: 12 }}>→ Firmenprofil anlegen</button></Link>
              </div>
            )}
            {ideas !== null && !needsProfile && ideas.length === 0 && !genLoading && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '16px 8px' }}>
                <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.4 }}>✦</div>
                <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>Noch keine KI-Ideen</p>
                <button onClick={() => handleGenerate(false)} disabled={genLoading} className="btn-primary" style={{ fontSize: 13 }}>✦ KI-Ideen generieren</button>
              </div>
            )}
            {genLoading && ideas?.length === 0 && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                <Spinner size={18} />
                <p style={{ fontSize: 13, color: 'var(--cyan)' }}>KI analysiert dein Profil...</p>
              </div>
            )}
            {genError && <div style={{ padding: '8px 12px', background: 'rgba(255,0,85,0.08)', border: '1px solid rgba(255,0,85,0.2)', borderRadius: 8, marginBottom: 10 }}><p style={{ fontSize: 12, color: 'var(--red)' }}>{genError}</p></div>}
            {ideas !== null && ideas.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                {ideas.map((idea, i) => (
                  <button key={i} onClick={() => navigate('/generate', { state: { prefillTopic: idea.text } })}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 13px', background: 'var(--glass2)', borderRadius: 10, border: '1px solid var(--glass-border)', cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left', width: '100%' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--cyan-dim)'; e.currentTarget.style.borderColor = 'rgba(0,255,255,0.25)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--glass2)'; e.currentTarget.style.borderColor = 'var(--glass-border)'; e.currentTarget.style.transform = 'none'; }}>
                    <span style={{ fontSize: 20, flexShrink: 0, lineHeight: 1.2 }}>{idea.icon || '💡'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5, marginBottom: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{idea.text}</p>
                      <span style={{ fontSize: 10, color: 'var(--cyan)' }}>✦ Post generieren</span>
                    </div>
                    {idea.type && <span style={{ fontSize: 9, color: 'var(--muted)', flexShrink: 0, alignSelf: 'flex-start', marginTop: 2 }}>{idea.type}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          {ideasSrc === 'weekly' && <div style={{ marginTop: 10, fontSize: 10, color: 'var(--muted)', textAlign: 'center' }}>Automatisch jeden Montag neu · basiert auf deinem Firmenprofil</div>}
        </div>
      </div>

      <div className="fade-up glass" style={{ marginBottom: 16, padding: '22px 26px', animationDelay: '0.13s' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text2)', letterSpacing: '0.1em' }}>WEBSITE ANALYTICS</span>
              <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 8, background: 'rgba(234,67,53,0.12)', color: '#ea4335', border: '1px solid rgba(234,67,53,0.25)', fontWeight: 700 }}>GA4</span>
            </div>
            <h2 style={{ fontSize: 16, fontWeight: 700 }}>Google Analytics</h2>
          </div>
          {ga4Data && (
            <div style={{ display: 'flex', gap: 4 }}>
              {[7, 30, 90].map(d => (
                <button key={d} onClick={() => setGa4Days(d)} style={{ padding: '4px 12px', borderRadius: 16, fontSize: 11, cursor: 'pointer', border: `1px solid ${ga4Days === d ? '#ea4335' : 'var(--glass-border)'}`, background: ga4Days === d ? 'rgba(234,67,53,0.1)' : 'transparent', color: ga4Days === d ? '#ea4335' : 'var(--muted)', fontWeight: ga4Days === d ? 700 : 400 }}>{d}T</button>
              ))}
            </div>
          )}
        </div>

        {ga4Loading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '32px 0' }}>
            <Spinner size={16} /><span style={{ fontSize: 13, color: 'var(--muted)' }}>GA4-Daten laden...</span>
          </div>
        )}

        {!ga4Loading && ga4Data === null && (
          <div style={{ textAlign: 'center', padding: '28px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.3 }}>📊</div>
            <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 6 }}>Google Analytics nicht verbunden</p>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>Verbinde dein GA4-Property um Website-Traffic direkt im Dashboard zu sehen.</p>
            <Link to="/settings?tab=analytics"><button className="btn-ghost" style={{ fontSize: 12 }}>Analytics einrichten →</button></Link>
          </div>
        )}

        {!ga4Loading && ga4Data && (
          <>
            {ga4Summary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 20 }}>
                {[
                  ['Sessions',        ga4Summary.sessions.toLocaleString('de-DE'),             '#ea4335'],
                  ['Nutzer',          ga4Summary.users.toLocaleString('de-DE'),                 '#4285f4'],
                  ['Seitenaufrufe',   ga4Summary.pageviews.toLocaleString('de-DE'),             '#34a853'],
                  ['Bounce Rate',     fmtBounce(ga4Summary.bounceRate),                         'var(--gold)'],
                  ['Ø Sitzungsdauer', fmtDuration(ga4Summary.avgSessionDuration),               'var(--cyan)'],
                  ['Neue Nutzer',     ga4Summary.newUsers.toLocaleString('de-DE'),              '#aa47bc'],
                ].map(([label, val, color]) => (
                  <div key={label} style={{ padding: '10px 14px', background: 'var(--glass2)', borderRadius: 10, border: `1px solid ${color}22` }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color, fontFamily: 'Orbitron', lineHeight: 1.1 }}>{val}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="ga4-grid" style={{ display: 'grid', gap: 16 }}>
              {ga4Series.length > 0 && (
                <div>
                  <p style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, letterSpacing: '0.08em', marginBottom: 10 }}>SESSIONS & NUTZER (TÄGLICH)</p>
                  <div style={{ height: 160 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={ga4Series}>
                        <defs>
                          <linearGradient id="gaSessions" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ea4335" stopOpacity={0.3}/><stop offset="95%" stopColor="#ea4335" stopOpacity={0}/></linearGradient>
                          <linearGradient id="gaUsers"    x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4285f4" stopOpacity={0.25}/><stop offset="95%" stopColor="#4285f4" stopOpacity={0}/></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--glass-border)" />
                        <XAxis dataKey="day" stroke="var(--muted)" tick={{ fill: 'var(--text2)', fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis stroke="var(--muted)" tick={{ fill: 'var(--text2)', fontSize: 10 }} domain={[0,'auto']} />
                        <Tooltip contentStyle={{ background: isDark ? 'rgba(10,10,20,0.95)' : 'rgba(240,248,255,0.97)', border: '1px solid rgba(234,67,53,0.25)', borderRadius: 10, fontSize: 12, color: 'var(--text)' }} />
                        <Area type="monotone" dataKey="sessions" stroke="#ea4335" strokeWidth={2}   fill="url(#gaSessions)" name="Sessions" />
                        <Area type="monotone" dataKey="users"    stroke="#4285f4" strokeWidth={1.5} fill="url(#gaUsers)"    name="Nutzer" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {ga4Channels.length > 0 && (
                  <div>
                    <p style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, letterSpacing: '0.08em', marginBottom: 10 }}>TRAFFIC-KANÄLE</p>
                    {ga4Channels.map((ch, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <div style={{ width: 96, fontSize: 11, color: 'var(--text2)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.channel}</div>
                        <div style={{ flex: 1, height: 5, background: 'var(--glass-border)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.round((ch.sessions / (ga4Channels[0]?.sessions || 1)) * 100)}%`, background: 'linear-gradient(90deg, #ea4335, #4285f4)', borderRadius: 3, opacity: 0.75 }} />
                        </div>
                        <div style={{ width: 40, fontSize: 11, fontWeight: 700, color: 'var(--text)', textAlign: 'right', flexShrink: 0 }}>{ch.sessions.toLocaleString('de-DE')}</div>
                      </div>
                    ))}
                  </div>
                )}

                {ga4Pages.length > 0 && (
                  <div>
                    <p style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, letterSpacing: '0.08em', marginBottom: 10 }}>TOP SEITEN</p>
                    {ga4Pages.map((pg, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pg.path}</div>
                          <div style={{ fontSize: 10, color: 'var(--muted)' }}>{fmtDuration(pg.avgTime)} Ø</div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#34a853', flexShrink: 0 }}>{pg.views.toLocaleString('de-DE')}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="fade-up dash-bottom" style={{ display: 'grid', gap: 16, animationDelay: '0.15s' }}>
        <div className="glass" style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700 }}>Letzte Posts</h2>
            <Link to="/history"><span style={{ fontSize: 13, color: 'var(--cyan)', cursor: 'pointer' }}>Alle anzeigen ›</span></Link>
          </div>
          {loading ? (
            [1,2,3].map(i => <div key={i} className="shimmer" style={{ height: 52, marginBottom: 8, borderRadius: 10 }} />)
          ) : (() => {
            const published    = posts.filter(p => p.variants?.some(v => v.published));
            const display      = published.length > 0 ? published.slice(0, 4) : posts.slice(0, 4);
            const showingDrafts = published.length === 0 && posts.length > 0;
            return display.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.3 }}>✦</div>
                <p style={{ fontSize: 14, color: 'var(--muted)' }}>Noch keine Posts</p>
                <Link to="/generate"><button className="btn-primary" style={{ marginTop: 12, fontSize: 13 }}>Ersten Post generieren</button></Link>
              </div>
            ) : (
              <>
                {showingDrafts && <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>Noch keine veroeffentlichten Posts — zeige letzte Entwuerfe</p>}
                {display.map((post, i) => {
                  const bgs = ['linear-gradient(135deg,#00ffff,#0066ff)','linear-gradient(135deg,#aa00ff,#ff0066)','linear-gradient(135deg,#00ff88,#00aaff)','linear-gradient(135deg,#ffaa00,#ff4400)'];
                  const isPublished = post.variants?.some(v => v.published);
                  return (
                    <div key={post.id} onClick={() => navigate('/history', { state: { expandPostId: post.id } })}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--glass2)', borderRadius: 10, marginBottom: 8, border: '1px solid var(--glass-border)', cursor: 'pointer', transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--cyan-dim)'; e.currentTarget.style.borderColor = 'rgba(0,255,255,0.2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'var(--glass2)'; e.currentTarget.style.borderColor = 'var(--glass-border)'; }}>
                      <div style={{ width: 36, height: 36, borderRadius: 9, background: bgs[i % bgs.length], flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }} className="truncate">{post.topic}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{post.variants?.length || 0} Varianten · {new Date(post.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })}</div>
                      </div>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, flexShrink: 0, fontWeight: 600, background: isPublished ? 'rgba(0,255,136,0.1)' : 'var(--glass)', color: isPublished ? 'var(--green)' : 'var(--muted)', border: `1px solid ${isPublished ? 'rgba(0,255,136,0.2)' : 'var(--glass-border)'}` }}>
                        {isPublished ? '✓ Veroeffentlicht' : 'Entwurf'}
                      </span>
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>
        <div style={{ minWidth: 0 }}><GlassCalendar posts={posts} scheduledPosts={scheduledPosts} /></div>
      </div>

      <style>{`
        .dash-stats { grid-template-columns: repeat(4, minmax(0,1fr)); }
        @media (max-width: 800px) { .dash-stats { grid-template-columns: repeat(2, minmax(0,1fr)) !important; } }
        .dash-top { grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); }
        @media (max-width: 960px) { .dash-top { grid-template-columns: minmax(0,1fr) !important; } }
        .dash-bottom { grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr); }
        @media (max-width: 800px) { .dash-bottom { grid-template-columns: minmax(0,1fr) !important; } }
        .ga4-grid { grid-template-columns: minmax(0,1fr); }
        @media (min-width: 900px) { .ga4-grid { grid-template-columns: minmax(0,3fr) minmax(0,2fr) !important; } }
      `}</style>
    </div>
  );
}

function StatCard({ title, value, change, positive, icon, color, delay }) {
  return (
    <div className="fade-up glass" style={{ padding: '20px 22px', animationDelay: `${delay}s`, transition: 'transform 0.2s' }}
      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: `${color}12`, border: `1px solid ${color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color }}>{icon}</div>
        <div style={{ padding: '4px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: positive ? 'rgba(0,255,136,0.1)' : 'rgba(255,170,0,0.1)', color: positive ? 'var(--green)' : 'var(--gold)', border: `1px solid ${positive ? 'rgba(0,255,136,0.2)' : 'rgba(255,170,0,0.2)'}`, whiteSpace: 'nowrap' }}>{change}</div>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4, fontFamily: 'Outfit' }}>{title}</div>
      <div style={{ fontSize: 36, fontFamily: 'Orbitron', fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function Spinner({ size = 12 }) {
  return <span style={{ width: size, height: size, border: `2px solid rgba(0,255,255,0.2)`, borderTopColor: 'var(--cyan)', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block', flexShrink: 0 }} />;
}
